import { HubConnection, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import { useEffect, useReducer, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../state/store";
import { parseDateTimes } from "../../utilities/helpers/parseDateTimes";
import { useLatestRef } from "./useLatestRef";

export type SignalRState = {
  connection: HubConnection | null;
  isConnecting: boolean;
  isReconnecting: boolean;
  error: string | null;
};

type SignalRAction =
  | { type: "CONNECT_START" }
  | { type: "CONNECT_SUCCESS"; payload: HubConnection }
  | { type: "CONNECT_ERROR"; payload: string }
  | { type: "RECONNECT_START" }
  | { type: "RECONNECT_SUCCESS" }
  | { type: "DISCONNECTED"; payload?: string };

function attachDateInterceptor(connection: HubConnection) {
  const originalOn = connection.on.bind(connection);

  connection.on = (methodName: string, newCallback: (...args: any[]) => void) => {
    return originalOn(methodName, (...args: any[]) => {
      const revivedArgs = args.map(arg => {
        if (typeof arg === "object" && arg !== null) {
          parseDateTimes(arg);
          return arg;
        }
        return arg;
      });

      newCallback(...revivedArgs);
    });
  };
}

export function signalRReducer(state: SignalRState, action: SignalRAction): SignalRState {
  switch (action.type) {
    case "CONNECT_START":
      return { ...state, isConnecting: true, isReconnecting: false };
    case "CONNECT_SUCCESS":
      return { ...state, connection: action.payload, isConnecting: false, isReconnecting: false, error: null };
    case "CONNECT_ERROR":
      return { ...state, isConnecting: false, isReconnecting: false, error: action.payload };
    case "RECONNECT_START":
      return { ...state, isReconnecting: true, error: null };
    case "RECONNECT_SUCCESS":
      return { ...state, isReconnecting: false, error: null };
    case "DISCONNECTED":
      return { connection: null, isConnecting: false, isReconnecting: false, error: action.payload ?? null };
    default:
      return state;
  }
}

export function useSignalR(hubUrl: string) {
  const [state, dispatch] = useReducer(signalRReducer, {
    connection: null,
    isConnecting: false,
    isReconnecting: false,
    error: null,
  });

  const currentUserPushNotificationPreferences = useSelector((state: RootState) => state.authReducer.user?.pushNotificationEnabled);
  const userId = useSelector((state: RootState) => state.authReducer.user?.id);
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const token = useSelector((state: RootState) => state.authReducer.accessToken);

  const userIdRef = useLatestRef(userId);
  const currentViewRef = useLatestRef(currentView);
  const connectionRef = useRef<HubConnection | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, { accessTokenFactory: () => token })
      .withAutomaticReconnect()
      .build();

    attachDateInterceptor(connection);
    connectionRef.current = connection;

    connection.onreconnecting((error) => {
      console.warn("Connection lost. Reconnecting...", error);
      dispatch({ type: "RECONNECT_START" });
    });

    connection.onreconnected(() => {
      const uid = userIdRef.current;
      if (!uid) {
        dispatch({ type: "RECONNECT_SUCCESS" });
        return;
      }
      connection
        .invoke("RegisterUserAsync")
        .then(() => connection.invoke("ChangeViewAsync", currentViewRef.current ?? "None"))
        .then(() => dispatch({ type: "RECONNECT_SUCCESS" }))
        .catch((err) => {
          console.error("Reconnection registration failed", err);
          dispatch({ type: "CONNECT_ERROR", payload: err.message });
        });
    });

    connection.onclose((error) => {
      if (cancelled) return;
      console.error("Connection closed due to error:", error);
      dispatch({ type: "DISCONNECTED", payload: error?.message ?? "Connection closed" });
    });

    dispatch({ type: "CONNECT_START" });

    const startPromise = connection.start();

    startPromise
      .then(() => {
        if (cancelled) return;
        const uid = userIdRef.current;
        if (!uid) return;
        return connection.invoke("RegisterUserAsync");
      })
      .then(() => {
        if (cancelled) return;
        const view = currentViewRef.current;
        if (!view) return;
        return connection.invoke("ChangeViewAsync", view);
      })
      .then(() => {
        if (cancelled) return;
        dispatch({ type: "CONNECT_SUCCESS", payload: connection });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Connection failed:", err);
        dispatch({ type: "CONNECT_ERROR", payload: err.message });
      });

    return () => {
      cancelled = true;
      connectionRef.current = null;
      startPromise
        .then(() => connection.stop())
        .catch(() => { /* start already failed; nothing to stop */ });
    };
  }, [hubUrl, token, userIdRef, currentViewRef, retryNonce]);

  useEffect(() => {
    if (!token || !state.error) return;

    const retry = () => {
      if (navigator.onLine) setRetryNonce((n) => n + 1);
    };

    window.addEventListener("online", retry);
    const intervalId = window.setInterval(retry, 20000);

    return () => {
      window.removeEventListener("online", retry);
      window.clearInterval(intervalId);
    };
  }, [state.error, token]);

  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || connection.state !== HubConnectionState.Connected || !currentView) return;
    connection
      .invoke("ChangeViewAsync", currentView)
      .catch((err) => console.error("ChangeViewAsync failed", err));
  }, [currentView, state.connection]);

  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || connection.state !== HubConnectionState.Connected) return;
    connection
      .invoke("ChangePushPreferenceAsync", currentUserPushNotificationPreferences ?? false)
      .catch((err) => console.error("ChangePushPreferenceAsync failed", err));
  }, [currentUserPushNotificationPreferences, state.connection]);

  return state;
}
